<template>
  <div class="min-h-screen">
    <AppNavbar title="Inbox" :back="true" />

    <main class="max-w-7xl mx-auto px-8 py-8">

      <!-- Page header -->
      <div class="mb-8">
        <div class="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <span>Dashboard</span>
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
          <span class="text-slate-600 font-medium">Inbox</span>
        </div>
        <div class="flex items-center justify-between gap-4">
          <div>
            <h1 class="text-3xl font-bold text-slate-800">Inbox</h1>
            <p class="text-slate-500 mt-1.5 text-base">Cab-ready alerts and discount notifications from the platform.</p>
          </div>
          <button
            @click="loadNotifications"
            class="flex-shrink-0 flex items-center gap-2 bg-white border border-slate-200 text-slate-600 hover:text-violet-600 hover:border-violet-200 hover:bg-violet-50 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm"
          >
            <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      <!-- Two-column layout: list + info panel -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

        <!-- Notification list (takes 2 of 3 cols) -->
        <div class="lg:col-span-2">
          <!-- Loading -->
          <div v-if="loading" class="bg-white border border-slate-100 rounded-2xl shadow-sm p-12 flex flex-col items-center justify-center">
            <svg class="w-6 h-6 animate-spin text-violet-500 mb-3" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <p class="text-sm text-slate-500">Loading notifications…</p>
          </div>

          <!-- Empty state -->
          <div v-else-if="notifications.length === 0 && !error" class="bg-white border border-slate-100 rounded-2xl shadow-sm p-12 flex flex-col items-center justify-center text-center">
            <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-slate-700 mb-1">All clear</h3>
            <p class="text-sm text-slate-400 max-w-xs">You have no notifications right now. They'll appear here when your cab is ready or a discount is available.</p>
          </div>

          <!-- List -->
          <div v-else class="space-y-3">
            <NotificationCard
              v-for="notification in notifications"
              :key="notification._id"
              :notification="notification"
            />
          </div>

          <AlertBanner v-if="error" :message="error" type="error" class="mt-4" />
        </div>

        <!-- Right panel: summary + legend -->
        <div class="space-y-4">
          <!-- Count summary card -->
          <div class="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
            <h3 class="text-sm font-semibold text-slate-700 mb-4">Summary</h3>
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                  <span class="text-sm text-slate-600">Cab-ready</span>
                </div>
                <span class="text-sm font-bold text-slate-800">{{ cabReadyCount }}</span>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                  <span class="text-sm text-slate-600">Discounts</span>
                </div>
                <span class="text-sm font-bold text-slate-800">{{ discountCount }}</span>
              </div>
              <div class="pt-3 border-t border-slate-100 flex items-center justify-between">
                <span class="text-sm font-semibold text-slate-600">Total</span>
                <span class="text-sm font-bold text-slate-800">{{ notifications.length }}</span>
              </div>
            </div>
          </div>

          <!-- Info card -->
          <div class="bg-violet-50 border border-violet-100 rounded-2xl p-6">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-7 h-7 bg-violet-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 class="text-sm font-semibold text-violet-800">About notifications</h3>
            </div>
            <p class="text-xs text-violet-700 leading-relaxed">
              Notifications are sent automatically when a cab is assigned to your booking, or when a discount is available for your route.
            </p>
          </div>
        </div>

      </div>
    </main>
  </div>
</template>

<script>
import AppNavbar from "@/components/AppNavbar.vue";
import NotificationCard from "@/components/NotificationCard.vue";
import AlertBanner from "@/components/AlertBanner.vue";

export default {
  name: "NotificationsView",
  components: { AppNavbar, NotificationCard, AlertBanner },
  data() {
    return {
      notifications: [],
      error: "",
      loading: false,
    };
  },
  computed: {
    cabReadyCount() {
      return this.notifications.filter(n => (n.type || '').toLowerCase().includes('cab')).length;
    },
    discountCount() {
      return this.notifications.filter(n => !(n.type || '').toLowerCase().includes('cab')).length;
    },
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push("/login");
      return;
    }
    this.loadNotifications();
  },
  methods: {
    async loadNotifications() {
      try {
        this.loading = true;
        this.error = "";
        const response = await this.$store.dispatch("fetchNotifications");
        this.notifications = response.data.notifications;
      } catch (error) {
        this.error = error.response?.data?.message || "Could not load notifications";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
